import { useElementSize } from '@/hooks/useElementSize'
import { FunnelCongruenceAnalysisResponse } from '@/routes/feeds.creative-toolkit.funnel-creative-analyzer.index'
import { CheckCircle, Heart, Pen, Star, X } from '@mynaui/icons-react'
import { FunnelCongruenceTabs } from './FCA'
import { Button } from './shadcn/button'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from './shadcn/drawer'
import { ScrollArea } from './shadcn/scroll-area'
import { Separator } from './shadcn/separator'

export default function InspirationAnalysisDetails({
  open,
  onOpenChange,
  data,
  showUseThisFormatButton = false,
}: {
  open: boolean
  onOpenChange: () => void
  data: FunnelCongruenceAnalysisResponse
  showUseThisFormatButton?: boolean
}) {
  const [squareRef, { width = 0 }] = useElementSize()

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>
            <div className="flex justify-end h-4 min-h-4">
              <span onClick={onOpenChange} className="p-1 cursor-pointer">
                <X className="w-4 h-4 min-w-4" />
              </span>
            </div>
          </DrawerTitle>
        </DrawerHeader>
        <div ref={squareRef} className="h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center">
              <div className="space-y-3">
                <h3 className="text-2xl font-semibold">E-commerce Template</h3>
                <p className="text-thememutedforeground">
                  Analyze this ad and landing page pair to see what makes it effective
                </p>
              </div>
              {showUseThisFormatButton && (
                <div>
                  <Button variant="destructivePurple" size="sm">
                    Use this Format
                  </Button>
                </div>
              )}
            </div>
            <Separator className="mt-4 mb-6" />
            <div className="mb-8 rounded-md border bg-slate-50 p-5">
              <div className="grid grid-cols-2 gap-5 lg:flex lg:gap-24">
                <div className="space-y-5 font-medium">
                  <div className="text-thememutedforeground flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Score</span>
                  </div>
                  <div className="flex">
                    <div className="p-0.5 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
                      <div className="rounded-md px-3.5 py-1.5 bg-white font-semibold text-black">
                        Score:{' '}
                        <span className="text-orange-600">
                          {data.analysis.coherence_evaluation.overall_score.score}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-5 font-medium">
                  <div className="text-thememutedforeground flex items-center gap-2">
                    <Star className="w-5 h-5" />
                    <span>Grade</span>
                  </div>
                  <div>
                    <p>{data.analysis.coherence_evaluation.overall_score.grade}</p>
                  </div>
                </div>
                <div className="col-span-2 space-y-5 font-medium">
                  <div className="text-thememutedforeground flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    <span>Emotional Label</span>
                  </div>
                  <div>
                    <p>{data.analysis.coherence_evaluation.overall_score.emotional_label}</p>
                  </div>
                </div>
                <div className="col-span-2 space-y-5 font-medium">
                  <div className="text-thememutedforeground flex items-center gap-2">
                    <Pen className="w-5 h-5" />
                    <span>Viseral Description</span>
                  </div>
                  <div>
                    <p>{data.analysis.coherence_evaluation.overall_score.visceral_description}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-2 space-y-6 rounded-lg p-6 border">
                  <h3 className="font-semibold text-lg">Audience Details</h3>
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm">Ad Preview</h3>
                    <div>
                      {typeof data.ad_images_data === 'string' && (
                        <img
                          src={data.ad_images_data}
                          alt=""
                          className="w-full h-[30svh] lg:h-[50svh] object-contain"
                        />
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm">Landing Page Preview</h3>
                    <ScrollArea className="h-[30svh] lg:h-[50svh]">
                      <img src={data.landing_page_image_data} alt="" />
                    </ScrollArea>
                  </div>
                </div>
                <div className="lg:col-span-3 rounded-lg border">
                  <FunnelCongruenceTabs
                    funnelCongruenceAnalysis={{
                      ...data,
                      landing_page_image_data: data.landing_page_image_data ?? '',
                      ad_images_data: data.ad_images_data ?? '',
                    }}
                    width={width}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
